import { uiText, useUiLanguage } from '../../../../utils/uiTranslation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RecruitmentFilterBar from '../AnalyticsRecruitment/components/RecruitmentFilterBar';
import { getAnalyticsScreenDefaults, ANALYTICS_SCREEN_IDS } from '../../analyticsScreenDefaults';
import { getAnalyticsToolbarActions } from '../../analyticsToolbar';
import {
  buildScreenFilterModels,
  canUseMaterializedAnalyticsBundle,
  getSupportedAnalyticsFilterState,
  normalizeAnalyticsScreenFilterOptions,
  useSessionAnalyticsFilters
} from '../../analyticsFilters';
import { useAnalyticsWidgetData, useQueriedAnalyticsWidgetData, useSharedAnalyticsDashboard } from '../../analyticsRtdb';
import ExecutiveKpiBar from './components/ExecutiveKpiBar';
import AttentionCard from './components/AttentionCard';
import AIWeeklyBrief from './components/AIWeeklyBrief';
import HireOutcomes from './components/HireOutcomes';
import { atsAnalyticsApi } from '../../../../services/atsAnalyticsService';
import useCanonicalAnalyticsExport from '../../useCanonicalAnalyticsExport';

const COPY = {
  en: {
    kpis: [],
    headcountTitle: 'Headcount Targets',
    configureTargets: 'Configure targets',
    targetModalTitle: 'Quarterly headcount targets',
    targetModalSubtitle: 'Set department hiring goals for the current quarter. These targets expire at quarter end and are shared anywhere analytics needs hiring goals.',
    targetDepartmentLabel: 'Department',
    targetValueLabel: 'Target hires',
    targetAddDepartment: 'Add department',
    targetSave: 'Save targets',
    targetCancel: 'Cancel',
    targetLoading: 'Loading targets...',
    targetSaving: 'Saving...',
    targetEmptyDepartment: 'Department name',
    targetError: 'Could not save headcount targets.',
    statuses: {
      track: 'On Track',
      risk: 'At Risk',
      critical: 'Critical'
    },
    departments: [],
    attentionTitle: 'Requires Attention',
    seeAll: 'See all',
    attentionBodyLead: '',
    attentionBody: '',
    attentionAlerts: [],
    feedTitle: 'Urgent alerts',
    feedSubtitle: 'Review all urgent items and decide what needs intervention first.',
    feedClose: 'Close',
    weeklyBriefTitle: 'AI Brief',
    weeklyBriefBody: '',
    focalPointsTitle: 'Focal Points',
    focalPointsBody: '',
    focalPointActions: [],
    hireOutcomesTitle: 'Funnel Intelligence',
    hireOutcomesSubtitle: '',
    hireOutcomesMetrics: [],
    prev: 'Prev',
    next: 'Next'
  },
  ar: {
    kpis: [],
    headcountTitle: 'أهداف التوظيف',
    configureTargets: 'تكوين الأهداف',
    targetModalTitle: 'أهداف التوظيف الربع سنوية',
    targetModalSubtitle: 'حدد أهداف التوظيف لكل قسم للربع الحالي. تنتهي هذه الأهداف بنهاية الربع وتستخدمها كل التحليلات التي تحتاج أهداف التوظيف.',
    targetDepartmentLabel: 'القسم',
    targetValueLabel: 'هدف التوظيف',
    targetAddDepartment: 'إضافة قسم',
    targetSave: 'حفظ الأهداف',
    targetCancel: 'إلغاء',
    targetLoading: 'جاري تحميل الأهداف...',
    targetSaving: 'جاري الحفظ...',
    targetEmptyDepartment: 'اسم القسم',
    targetError: 'تعذر حفظ أهداف التوظيف.',
    statuses: {
      track: 'على المسار',
      risk: 'في خطر',
      critical: 'حرج'
    },
    departments: [],
    attentionTitle: 'يتطلب انتباهًا',
    seeAll: 'عرض الكل',
    attentionBodyLead: '',
    attentionBody: '',
    attentionAlerts: [],
    feedTitle: 'التنبيهات العاجلة',
    feedSubtitle: 'راجع جميع العناصر العاجلة وحدد ما يحتاج إلى تدخل أولاً.',
    feedClose: 'إغلاق',
    weeklyBriefTitle: 'ملخص الذكاء الاصطناعي',
    weeklyBriefBody: '',
    focalPointsTitle: 'نقاط التركيز',
    focalPointsBody: '',
    focalPointActions: [],
    hireOutcomesTitle: 'ذكاء مسار التوظيف',
    hireOutcomesSubtitle: '',
    hireOutcomesMetrics: [],
    prev: 'السابق',
    next: 'التالي'
  }
};

const ProgressRing = ({ progress, tone }) => (
  <div
    className={`executive-overview-ring ${tone}`}
    style={{ '--progress': `${progress}%` }}
  >
    <div className="executive-overview-ring-inner">{progress}%</div>
  </div>
);

const SCREEN_DEFAULTS = getAnalyticsScreenDefaults(ANALYTICS_SCREEN_IDS.EXECUTIVE_OVERVIEW);
const OVERVIEW_WIDGET_FILTERS = {
  executive_kpi_bar: ['period'],
  headcount_targets: ['period'],
  requires_attention: [],
  ai_weekly_brief: ['period'],
  funnel_intelligence: ['period'],
  focal_points: []
};

const AI_BRIEF_PERIOD_LABELS = {
  en: {
    last_30_days: 'Last 30 days',
    last_60_days: 'Last 60 days',
    last_90_days: 'Last 90 days'
  },
  ar: {
    last_30_days: 'آخر 30 يومًا',
    last_60_days: 'آخر 60 يومًا',
    last_90_days: 'آخر 90 يومًا'
  }
};

const AI_BRIEF_CADENCE_LABELS = {
  en: {
    weekly: 'Weekly',
    biweekly: 'Biweekly'
  },
  ar: {
    weekly: 'أسبوعي',
    biweekly: 'كل أسبوعين'
  }
};

const normalizeAIBriefPeriod = (period) =>
  ['last_30_days', 'last_60_days', 'last_90_days'].includes(period) ? period : 'last_90_days';

const formatAIBriefTimestamp = (timestamp, isRTL = false) => {
  if (!timestamp) return '';
  const date = new Date(Number(timestamp));
  if (Number.isNaN(date.getTime())) return '';
  const formatter = new Intl.DateTimeFormat(isRTL ? 'ar' : 'en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  return `${isRTL ? 'آخر تحديث' : 'Updated'} ${formatter.format(date)}`;
};

const normalizeDraftDepartmentId = (label, index) =>
  String(label || `department-${index + 1}`)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || `department_${index + 1}`;

const normalizeTargetRows = (departments = []) =>
  (Array.isArray(departments) ? departments : [])
    .map((department, index) => {
      const label = String(department?.label || department?.department || department?.name || '').trim();
      return {
        id: String(department?.id || normalizeDraftDepartmentId(label, index)),
        label,
        target: Math.max(0, Number(department?.target || 0))
      };
    })
    .filter((department) => department.label);

const createSeedTargetRows = (departments = []) => {
  const rows = normalizeTargetRows(departments);
  return rows.length ? rows : [{ id: 'department_1', label: '', target: 0 }];
};

const normalizeHeadcountKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const getPlanDaysRemaining = (plan = {}) => {
  const expiresAt = plan?.expiresAt ? new Date(plan.expiresAt).getTime() : 0;
  if (!Number.isFinite(expiresAt) || expiresAt <= 0) return 0;
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / 86400000));
};

const getDepartmentActual = (department = {}) => {
  const directActual = Number(department.actual);
  if (Number.isFinite(directActual)) return Math.max(0, directActual);
  const filledActual = Number(String(department.filled || '').split('/')[0]);
  return Number.isFinite(filledActual) ? Math.max(0, filledActual) : 0;
};

const buildDepartmentIndex = (departments = []) => {
  const index = new Map();
  departments.forEach((department) => {
    [department?.id, department?.label].forEach((value) => {
      const key = normalizeHeadcountKey(value);
      if (key && !index.has(key)) {
        index.set(key, department);
      }
    });
  });
  return index;
};

const mergeHeadcountPlanDepartments = (departments = [], plan = {}) => {
  const targetRows = normalizeTargetRows(plan?.departments || []);
  if (!targetRows.length) return [];

  const existingDepartments = Array.isArray(departments) ? departments : [];
  const existingIndex = buildDepartmentIndex(existingDepartments);
  const planKeys = new Set(targetRows.flatMap((row) => [
    normalizeHeadcountKey(row.id),
    normalizeHeadcountKey(row.label)
  ]));
  const daysRemaining = getPlanDaysRemaining(plan);

  const configuredDepartments = targetRows.map((row, index) => {
    const existing =
      existingIndex.get(normalizeHeadcountKey(row.id)) ||
      existingIndex.get(normalizeHeadcountKey(row.label)) ||
      {};
    const target = Math.max(0, Number(row.target || 0));
    const actual = getDepartmentActual(existing);
    const progress = target ? Math.min(Math.round((actual / target) * 100), 100) : 0;
    const tone = progress >= 80 ? 'track' : progress >= 55 ? 'risk' : 'critical';

    return {
      ...existing,
      id: normalizeDraftDepartmentId(row.id || row.label, index),
      label: row.label,
      filled: target ? `${actual}/${target}` : `${actual}/--`,
      actual,
      target,
      pipeline: Math.max(0, Number(existing.pipeline || 0)),
      daysRemaining: Number.isFinite(Number(existing.daysRemaining)) ? Number(existing.daysRemaining) : daysRemaining,
      progress,
      tone,
      configured: target > 0
    };
  });

  const unconfiguredDepartments = existingDepartments.filter((department) => {
    const idKey = normalizeHeadcountKey(department?.id);
    const labelKey = normalizeHeadcountKey(department?.label);
    return !planKeys.has(idKey) && !planKeys.has(labelKey);
  });

  return [...configuredDepartments, ...unconfiguredDepartments];
};

const HeadcountTargetModal = ({
  copy,
  open,
  rows,
  periodLabel,
  loading,
  saving,
  error,
  isRTL,
  onClose,
  onSave,
  onAddRow,
  onRemoveRow,
  onChangeRow
}) => {
  useUiLanguage();
  if (!open) return null;

  return (
    <div className="executive-overview-modal-backdrop">
      <section className={`executive-overview-modal executive-overview-target-modal${isRTL ? ' rtl' : ''}`}>
        <div className="executive-overview-modal-head">
          <div>
            <h3>{copy.targetModalTitle}</h3>
            <p>{copy.targetModalSubtitle}</p>
            {periodLabel ? <p>{periodLabel}</p> : null}
          </div>
          <button type="button" className="executive-overview-modal-close" onClick={onClose}>
            {copy.targetCancel}
          </button>
        </div>

        <div className="executive-overview-target-form">
          {loading ? (
            <div className="executive-overview-empty-state">{copy.targetLoading}</div>
          ) : (
            <>
              <div className="executive-overview-target-form-head">
                <span>{copy.targetDepartmentLabel}</span>
                <span>{copy.targetValueLabel}</span>
                <span aria-hidden="true" />
              </div>

              <div className="executive-overview-target-form-rows">
                {rows.map((row, index) => (
                  <div key={row.id || index} className="executive-overview-target-form-row">
                    <input
                      type="text"
                      value={row.label}
                      placeholder={copy.targetEmptyDepartment}
                      onChange={(event) => onChangeRow(index, { label: event.target.value })}
                    />
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={row.target}
                      onChange={(event) => onChangeRow(index, { target: event.target.value })}
                    />
                    <button
                      type="button"
                      className="executive-overview-target-row-remove"
                      onClick={() => onRemoveRow(index)}
                      aria-label={uiText("Remove department")}
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                ))}
              </div>

              {error ? <div className="executive-overview-target-error">{uiText(error)}</div> : null}

              <div className="executive-overview-target-form-actions">
                <button type="button" className="executive-overview-target-secondary" onClick={onAddRow}>
                  <i className="fa-solid fa-plus" />
                  <span>{copy.targetAddDepartment}</span>
                </button>
                <button type="button" className="executive-overview-target-primary" onClick={onSave} disabled={saving}>
                  {saving ? copy.targetSaving : copy.targetSave}
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

const ExecutiveOverview = ({ isRTL = false, companyUID = '', analyticsActorId = 'system' }) => {
  const baseCopy = isRTL ? COPY.ar : COPY.en;
  const { filterState, setFilterValue } = useSessionAnalyticsFilters({ screenId: SCREEN_DEFAULTS.screenId });
  const selectedAIBriefPeriod = normalizeAIBriefPeriod(filterState.period || 'last_90_days');
  const [aiBriefRefresh, setAIBriefRefresh] = useState({
    loading: false,
    message: '',
    widget: null
  });
  const [headcountSettingsOpen, setHeadcountSettingsOpen] = useState(false);
  const [headcountSettingsLoading, setHeadcountSettingsLoading] = useState(false);
  const [headcountSettingsSaving, setHeadcountSettingsSaving] = useState(false);
  const [headcountSettingsError, setHeadcountSettingsError] = useState('');
  const [headcountPlan, setHeadcountPlan] = useState(null);
  const [headcountPlanLoaded, setHeadcountPlanLoaded] = useState(!companyUID);
  const [headcountTargetRows, setHeadcountTargetRows] = useState([{ id: 'department_1', label: '', target: 0 }]);
  const [headcountRevision, setHeadcountRevision] = useState(0);
  const nativeWidgetIds = useMemo(() => ['executive_kpi_bar', 'headcount_targets', 'funnel_intelligence', 'requires_attention', 'ai_weekly_brief', 'focal_points'], []);
  useSharedAnalyticsDashboard({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    defaultVisibleWidgetIds: SCREEN_DEFAULTS.defaultVisibleWidgetIds,
    defaultWidgetPrefs: SCREEN_DEFAULTS.defaultWidgetPrefs,
    locked: SCREEN_DEFAULTS.locked,
    updatedBy: analyticsActorId
  });
  const { widgetNodes: nativeWidgetNodes, loading: nativeWidgetsLoading } = useAnalyticsWidgetData({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    widgetIds: nativeWidgetIds
  });
  const fallbackFilterOptions = useMemo(() => ({}), []);
  const filterOptions = useMemo(
    () =>
      normalizeAnalyticsScreenFilterOptions({
        screenId: SCREEN_DEFAULTS.screenId,
        isRTL,
        remoteOptions: {},
        fallbackOptions: fallbackFilterOptions
      }),
    [fallbackFilterOptions, isRTL]
  );
  const filterModels = useMemo(
    () =>
      buildScreenFilterModels({
        screenId: SCREEN_DEFAULTS.screenId,
        isRTL,
        filterState,
        filterOptions
      }),
    [filterOptions, filterState, isRTL]
  );
  const {
    exporting,
    exportMode,
    handleExportAction
  } = useCanonicalAnalyticsExport({
    screenId: SCREEN_DEFAULTS.screenId,
    companyUID,
    analyticsActorId,
    filterState,
    extraContext: { headcountPlan },
    language: isRTL ? 'ar' : 'en'
  });
  const toolbarActions = useMemo(
    () => getAnalyticsToolbarActions({
      isRTL,
      locked: SCREEN_DEFAULTS.locked,
      allowExport: true,
      exporting,
      exportMode
    }),
    [exportMode, exporting, isRTL]
  );
  const forceLiveOverviewQuery = headcountRevision > 0;
  const headcountGroupBy = 'department';
  const overviewWidgetFilters = useMemo(
    () => ({
      executive_kpi_bar: getSupportedAnalyticsFilterState({
        filterState,
        supportedFilters: OVERVIEW_WIDGET_FILTERS.executive_kpi_bar
      }),
      headcount_targets: getSupportedAnalyticsFilterState({
        filterState,
        supportedFilters: OVERVIEW_WIDGET_FILTERS.headcount_targets,
        groupBy: headcountGroupBy
      }),
      funnel_intelligence: getSupportedAnalyticsFilterState({
        filterState,
        supportedFilters: OVERVIEW_WIDGET_FILTERS.funnel_intelligence
      }),
      ai_weekly_brief: getSupportedAnalyticsFilterState({
        filterState: {
          ...filterState,
          period: selectedAIBriefPeriod
        },
        supportedFilters: OVERVIEW_WIDGET_FILTERS.ai_weekly_brief
      })
    }),
    [filterState, headcountGroupBy, selectedAIBriefPeriod]
  );
  const canUseMaterializedOverview = useMemo(
    () =>
      canUseMaterializedAnalyticsBundle({
        screenId: SCREEN_DEFAULTS.screenId,
        filterState,
        supportedFilters: OVERVIEW_WIDGET_FILTERS.executive_kpi_bar,
        supportsGroupBy: false
      }) && headcountGroupBy === 'department',
    [filterState, headcountGroupBy]
  );
  const hasMaterializedOverviewNodes = useMemo(
    () => ({
      executive_kpi_bar: Boolean(nativeWidgetNodes.executive_kpi_bar),
      headcount_targets: Boolean(nativeWidgetNodes.headcount_targets),
      funnel_intelligence: Boolean(nativeWidgetNodes.funnel_intelligence)
    }),
    [nativeWidgetNodes]
  );
  const { widgetNodes: queriedOverviewNodes, loading: queriedOverviewLoading } = useQueriedAnalyticsWidgetData({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    widgetQueryMap: {
      executive_kpi_bar: {
        enabled: Boolean(companyUID),
        filters: overviewWidgetFilters.executive_kpi_bar,
        groupBy: '',
        revision: headcountRevision
      },
      headcount_targets: {
        enabled: Boolean(companyUID),
        filters: overviewWidgetFilters.headcount_targets,
        groupBy: headcountGroupBy,
        revision: headcountRevision
      },
      funnel_intelligence: {
        enabled: Boolean(companyUID),
        filters: overviewWidgetFilters.funnel_intelligence,
        groupBy: '',
        revision: headcountRevision
      },
      requires_attention: {
        enabled: Boolean(companyUID) && forceLiveOverviewQuery,
        filters: {},
        groupBy: '',
        revision: headcountRevision
      }
    }
  });
  const isScreenLoading =
    Boolean(companyUID) &&
    (((nativeWidgetsLoading || queriedOverviewLoading) &&
        !Object.keys(nativeWidgetNodes).length &&
        !Object.keys(queriedOverviewNodes).length));

  const copy = useMemo(() => {
    const basePayload = {
      ...baseCopy,
      kpis: [],
      departments: [],
      attentionAlerts: [],
      weeklyBriefBody: '',
      weeklyBriefPeriodLabel: AI_BRIEF_PERIOD_LABELS[isRTL ? 'ar' : 'en'][selectedAIBriefPeriod],
      weeklyBriefCadenceLabel: '',
      weeklyBriefRefreshedLabel: '',
      focalPointActions: [],
      hireOutcomesMetrics: []
    };
    const canUseMaterializedKpi = canUseMaterializedOverview && hasMaterializedOverviewNodes.executive_kpi_bar;
    const canUseMaterializedHeadcount = canUseMaterializedOverview && hasMaterializedOverviewNodes.headcount_targets;
    const canUseMaterializedFunnel = canUseMaterializedOverview && hasMaterializedOverviewNodes.funnel_intelligence;
    const kpiPayload = (queriedOverviewNodes.executive_kpi_bar ||
      (canUseMaterializedKpi ? nativeWidgetNodes.executive_kpi_bar : null)
    )?.payload;
    const headcountPayload = (queriedOverviewNodes.headcount_targets ||
      (canUseMaterializedHeadcount ? nativeWidgetNodes.headcount_targets : null)
    )?.payload;
    const funnelPayload = (queriedOverviewNodes.funnel_intelligence ||
      (canUseMaterializedFunnel ? nativeWidgetNodes.funnel_intelligence : null)
    )?.payload;
    const attentionPayload = (forceLiveOverviewQuery && queriedOverviewNodes.requires_attention
      ? queriedOverviewNodes.requires_attention
      : nativeWidgetNodes.requires_attention
    )?.payload;
    const briefPayload = (aiBriefRefresh.widget || nativeWidgetNodes.ai_weekly_brief)?.payload;
    const briefPeriods = briefPayload?.periods && typeof briefPayload.periods === 'object' ? briefPayload.periods : {};
    const selectedBrief = briefPeriods[selectedAIBriefPeriod] || (
      briefPayload?.selectedBrief?.period === selectedAIBriefPeriod ? briefPayload.selectedBrief : null
    );
    const focalPayload = nativeWidgetNodes.focal_points?.payload;
    const cadence = selectedBrief?.cadence || (selectedAIBriefPeriod === 'last_90_days' ? 'biweekly' : 'weekly');
    const payloadDepartments = Array.isArray(headcountPayload?.departments) ? headcountPayload.departments : [];
    const headcountDepartments = headcountPlanLoaded
      ? mergeHeadcountPlanDepartments(payloadDepartments, headcountPlan)
      : [];

    return {
      ...basePayload,
      ...(Array.isArray(kpiPayload?.kpis) && kpiPayload.kpis.length ? { kpis: kpiPayload.kpis } : {}),
      ...(headcountDepartments.length
        ? { departments: headcountDepartments }
        : {}),
      ...(Array.isArray(funnelPayload?.metrics) && funnelPayload.metrics.length
        ? { hireOutcomesMetrics: funnelPayload.metrics }
        : {}),
      ...(Array.isArray(attentionPayload?.alerts) && attentionPayload.alerts.length
        ? { attentionAlerts: attentionPayload.alerts }
        : {}),
      ...(typeof briefPayload?.title === 'string' && briefPayload.title
        ? { weeklyBriefTitle: briefPayload.title }
        : {}),
      ...(typeof selectedBrief?.brief === 'string' && selectedBrief.brief
        ? { weeklyBriefBody: selectedBrief.brief }
        : typeof briefPayload?.brief === 'string' && briefPayload.brief
          ? { weeklyBriefBody: briefPayload.brief }
          : {}),
      ...(selectedBrief
        ? {
            weeklyBriefPeriodLabel: AI_BRIEF_PERIOD_LABELS[isRTL ? 'ar' : 'en'][selectedAIBriefPeriod] || selectedBrief.periodLabel,
            weeklyBriefCadenceLabel: AI_BRIEF_CADENCE_LABELS[isRTL ? 'ar' : 'en'][cadence] || cadence,
            weeklyBriefRefreshedLabel: formatAIBriefTimestamp(selectedBrief.lastRefreshedAt, isRTL)
          }
        : {}),
      ...(Array.isArray(focalPayload?.actions) && focalPayload.actions.length
        ? { focalPointActions: focalPayload.actions }
        : {})
    };
  }, [aiBriefRefresh.widget, baseCopy, canUseMaterializedOverview, forceLiveOverviewQuery, hasMaterializedOverviewNodes, headcountPlan, headcountPlanLoaded, isRTL, nativeWidgetNodes, queriedOverviewNodes, selectedAIBriefPeriod]);
  const headcountSeedDepartmentsRef = useRef([]);

  useEffect(() => {
    if (!headcountSettingsOpen) {
      headcountSeedDepartmentsRef.current = copy.departments || [];
    }
  }, [copy.departments, headcountSettingsOpen]);

  const handleAIBriefRefresh = useCallback(async () => {
    if (!companyUID || aiBriefRefresh.loading) return;

    setAIBriefRefresh((current) => ({
      ...current,
      loading: true,
      message: ''
    }));

    try {
      const response = await atsAnalyticsApi.requestAIBriefRefresh(companyUID, selectedAIBriefPeriod);
      setAIBriefRefresh({
        loading: false,
        message: response?.status === 'cooldown'
          ? (isRTL ? 'يمكن طلب تحديث آخر بعد بضع دقائق.' : 'Another refresh will be available in a few minutes.')
          : '',
        widget: response?.widget || null
      });
    } catch (error) {
      setAIBriefRefresh((current) => ({
        ...current,
        loading: false,
        message: isRTL ? 'تعذر تحديث الملخص الآن.' : 'Brief refresh failed.'
      }));
    }
  }, [aiBriefRefresh.loading, companyUID, isRTL, selectedAIBriefPeriod]);

  const openHeadcountSettings = useCallback(() => {
    setHeadcountSettingsError('');
    setHeadcountSettingsLoading(Boolean(companyUID));
    setHeadcountTargetRows([{ id: 'department_1', label: '', target: 0 }]);
    setHeadcountSettingsOpen(true);
  }, [companyUID]);

  useEffect(() => {
    if (!companyUID) {
      setHeadcountPlan(null);
      setHeadcountPlanLoaded(true);
      setHeadcountTargetRows([{ id: 'department_1', label: '', target: 0 }]);
      return undefined;
    }

    let cancelled = false;
    setHeadcountPlanLoaded(false);
    atsAnalyticsApi
      .getHeadcountTargets(companyUID)
      .then((response) => {
        if (cancelled) return;
        setHeadcountPlan(response?.plan || null);
        setHeadcountPlanLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setHeadcountPlan(null);
          setHeadcountPlanLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [companyUID, headcountRevision]);

  useEffect(() => {
    if (!headcountSettingsOpen || !companyUID) return undefined;

    let cancelled = false;
    setHeadcountSettingsLoading(true);
    setHeadcountSettingsError('');

    atsAnalyticsApi
      .getHeadcountTargets(companyUID)
      .then((response) => {
        if (cancelled) return;
        const plan = response?.plan || null;
        const planRows = normalizeTargetRows(plan?.departments || []);
        const seedRows = planRows.length ? planRows : createSeedTargetRows(headcountSeedDepartmentsRef.current || []);
        setHeadcountPlan(plan);
        setHeadcountPlanLoaded(true);
        setHeadcountTargetRows(seedRows);
        setHeadcountSettingsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setHeadcountPlan(null);
        setHeadcountPlanLoaded(true);
        setHeadcountSettingsError(copy.targetError);
        setHeadcountTargetRows(createSeedTargetRows(headcountSeedDepartmentsRef.current || []));
        setHeadcountSettingsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyUID, copy.targetError, headcountSettingsOpen]);

  const updateHeadcountTargetRow = useCallback((index, patch) => {
    setHeadcountTargetRows((current) =>
      current.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        const nextLabel = patch.label !== undefined ? patch.label : row.label;
        return {
          ...row,
          ...patch,
          id: patch.label !== undefined ? normalizeDraftDepartmentId(nextLabel, rowIndex) : row.id,
          target: patch.target !== undefined ? Math.max(0, Number(patch.target || 0)) : row.target
        };
      })
    );
  }, []);

  const addHeadcountTargetRow = useCallback(() => {
    setHeadcountTargetRows((current) => [
      ...current,
      { id: `department_${current.length + 1}`, label: '', target: 0 }
    ]);
  }, []);

  const removeHeadcountTargetRow = useCallback((index) => {
    setHeadcountTargetRows((current) => {
      const nextRows = current.filter((_, rowIndex) => rowIndex !== index);
      return nextRows.length ? nextRows : [{ id: 'department_1', label: '', target: 0 }];
    });
  }, []);

  const saveHeadcountTargets = useCallback(async () => {
    if (!companyUID || headcountSettingsSaving) return;

    setHeadcountSettingsSaving(true);
    setHeadcountSettingsError('');

    try {
      const departments = headcountTargetRows
        .map((row, index) => ({
          id: normalizeDraftDepartmentId(row.label || row.id, index),
          label: String(row.label || '').trim(),
          target: Math.max(0, Number(row.target || 0))
        }))
        .filter((row) => row.label);

      const response = await atsAnalyticsApi.saveHeadcountTargets(companyUID, {
        periodKey: headcountPlan?.periodKey,
        departments
      });
      const nextPlan = response?.plan || null;
      setHeadcountPlan(nextPlan);
      setHeadcountPlanLoaded(true);
      setHeadcountTargetRows(createSeedTargetRows(nextPlan?.departments || departments));
      setHeadcountRevision(Date.now());
      setHeadcountSettingsOpen(false);
    } catch (error) {
      setHeadcountSettingsError(copy.targetError);
    } finally {
      setHeadcountSettingsSaving(false);
    }
  }, [companyUID, copy.targetError, headcountPlan, headcountSettingsSaving, headcountTargetRows]);

  return (
    <section className={`executive-overview${isRTL ? ' rtl' : ''}`}>
      {isScreenLoading ? (
        <div className="data-analytics-loading-screen">
          <i className="fa-solid fa-spinner fa-spin" />
          <p>{isRTL ? 'يرجى الانتظار' : 'Please Wait'}</p>
        </div>
      ) : (
        <>
      <RecruitmentFilterBar
        filters={filterModels}
        actions={toolbarActions}
        onAction={handleExportAction}
        onFilterChange={setFilterValue}
        isRTL={isRTL}
      />

      <ExecutiveKpiBar kpis={copy.kpis} isRTL={isRTL} />

      <div className="executive-overview-split">
        <article className="executive-overview-panel executive-overview-targets">
          <div className="executive-overview-target-card-head">
            <div className="executive-overview-target-title-actions">
              <div className="executive-overview-card-title">{copy.headcountTitle}</div>
              <button
                type="button"
                className="executive-overview-headcount-settings"
                onClick={openHeadcountSettings}
              >
                <i className="fa-solid fa-gear" />
                <span>{copy.configureTargets}</span>
              </button>
            </div>
          </div>

          <div className="executive-overview-status-legend">
            <span className="track">{copy.statuses.track}</span>
            <span className="risk">{copy.statuses.risk}</span>
            <span className="critical">{copy.statuses.critical}</span>
          </div>

          <div className="executive-overview-target-grid">
            {companyUID && !headcountPlanLoaded ? (
              <div className="executive-overview-empty-state">
                {copy.targetLoading}
              </div>
            ) : copy.departments.length ? copy.departments.map((dept) => (
              <div key={dept.label} className="executive-overview-target-item">
                <ProgressRing progress={dept.progress} tone={dept.tone} />
                <div className="executive-overview-target-label">{uiText(dept.label)}</div>
                <div className="executive-overview-target-value">{dept.filled}</div>
              </div>
            )) : (
              <div className="executive-overview-empty-state">
                {isRTL ? 'لا توجد بيانات أهداف مباشرة متاحة بعد.' : 'No live target data is available yet.'}
              </div>
            )}
          </div>
        </article>

        <AttentionCard copy={copy} isRTL={isRTL} />
      </div>

      <div className="executive-overview-chart-row">
        <AIWeeklyBrief
          title={copy.weeklyBriefTitle}
          body={copy.weeklyBriefBody}
          periodLabel={copy.weeklyBriefPeriodLabel}
          cadenceLabel={copy.weeklyBriefCadenceLabel}
          refreshedLabel={copy.weeklyBriefRefreshedLabel}
          onRequestRefresh={handleAIBriefRefresh}
          refreshLoading={aiBriefRefresh.loading}
          refreshDisabled={!companyUID}
          refreshMessage={aiBriefRefresh.message}
          isRTL={isRTL}
        />
        <HireOutcomes
          title={copy.hireOutcomesTitle}
          subtitle={copy.hireOutcomesSubtitle}
          metrics={copy.hireOutcomesMetrics}
          isRTL={isRTL}
        />
      </div>
      <HeadcountTargetModal
        copy={copy}
        open={headcountSettingsOpen}
        rows={headcountTargetRows}
        periodLabel={headcountPlan?.periodLabel}
        loading={headcountSettingsLoading}
        saving={headcountSettingsSaving}
        error={headcountSettingsError}
        isRTL={isRTL}
        onClose={() => setHeadcountSettingsOpen(false)}
        onSave={saveHeadcountTargets}
        onAddRow={addHeadcountTargetRow}
        onRemoveRow={removeHeadcountTargetRow}
        onChangeRow={updateHeadcountTargetRow}
      />
{/* TODO: Uncomment this when the focal points are implemented 
      <div className="executive-overview-action-row">
        <FocalPoints
          title={copy.focalPointsTitle}
          body={copy.focalPointsBody}
          actions={copy.focalPointActions}
          isRTL={isRTL}
        />
      </div>
    */}
        </>
      )}
    </section>
  );
};

export default ExecutiveOverview;
